import * as React from "react";

import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import {
  Form,
  Link,
  useActionData,
  useSearchParams,
  useNavigation,
} from "@remix-run/react";
import { add } from "date-fns";
import { Spinner } from "flowbite-react";
import { parseFormAny, useZorm } from "react-zorm";
import { z } from "zod";

import LoginCard from "~/components/dashboard/LoginCard/LoginCard";
import {
  APP_PAGE_URL,
  DEFAULT_COUNTRY_ISO,
  MAINTENANCE_MESSAGE,
  MAINTENANCE_MODE,
  SUBSCRIBE_URL,
} from "~/Config/config";
import { db } from "~/database";
import { stripe } from "~/integrations/stripe";
import { isUnauthenticated } from "~/modules/auth/session.server";
import {
  createSubscription,
  fetchSubscription,
} from "~/modules/subscription/service.server";
import { subscriptionRoleByLimit } from "~/modules/subscription/subscription-role-by-limit";
import { getUserById } from "~/modules/user";
import { isFormProcessing } from "~/utils";
import { supabaseServerClient } from "~/integrations/supabase/server";
import { rewriteLegacyVerifyPath } from "~/modules/verify/legacy-verify-redirect.server";
import { safeRedirectPath } from "~/utils/safe-redirect";

export async function loader({ request }: LoaderFunctionArgs) {
  // While MAINTENANCE_MODE is on we still render the login page so admins
  // can sign in; non-admin logins are rejected in the action below.
  const userIsUnauthenticated = await isUnauthenticated(request);

  if (!userIsUnauthenticated) return redirect(APP_PAGE_URL);

  return json({});
}

const LoginFormSchema = z.object({
  email: z
    .string()
    .email("Invalid email")
    .transform((email) => email.toLowerCase()),
  password: z.string().min(8, "Password tooshort. Minimum 8 characters"),
  redirectTo: z.string().optional(),
});

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const result = await LoginFormSchema.safeParseAsync(parseFormAny(formData));

  const { supabase, headers } = supabaseServerClient(request);

  if (!result.success) {
    return json(
      {
        errors: result.error,
      },
      { status: 400 }
    );
  }

  const { email, password, redirectTo } = result.data;

  const {
    data: { user: userData },
    error,
  } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    return json(
      {
        errors: error.message || "Invalid email or password. Please try again",
      },
      { status: 400 }
    );
  }

  if (!userData?.id) {
    return json(
      {
        errors: "User not found",
      },
      { status: 400 }
    );
  }

  const user = await getUserById(userData?.id);

  // While maintenance mode is on, only admins are allowed to complete login.
  // Non-admins get signed out immediately so a stale Supabase session can't
  // sneak past the gate on a later request.
  if (MAINTENANCE_MODE && user?.role !== "ADMIN") {
    await supabase.auth.signOut();
    return json({ errors: MAINTENANCE_MESSAGE }, { status: 503 });
  }

  // Admins skip the Stripe path; during maintenance also force them to
  // /admin since /app/* is still blocked by requireAuthSession.
  if (MAINTENANCE_MODE && user?.role === "ADMIN") {
    return redirect("/admin", { headers: headers });
  }

  // If user doesn't have a subscription, check Stripe for a subscription and customer ID
  let customer;
  if (user?.role !== "ADMIN" && !user?.subscription) {
    customer = await stripe.customers
      .search({
        query: `email:'${email}'`,
        expand: ["data.subscriptions"],
      })
      .then((result) => result.data[0]);

    // If user doesn't have a customer ID, create one
    if (!customer) {
      customer = await stripe.customers.create({
        email,
        name: `${user?.firstName} ${user?.lastName}`,
        address: {
          country: user?.country || DEFAULT_COUNTRY_ISO,
        },
      });
    }

    // Update user with customer ID
    await db.user.update({
      where: { id: userData?.id },
      data: {
        customer_id: customer.id,
      },
    });

    // If user doesn't have subscription, redirect to subscription page
    if (
      !customer?.subscriptions?.data ||
      customer.subscriptions.data.length === 0
    ) {
      return redirect(SUBSCRIBE_URL, {
        headers: headers,
      });
    }

    const subscriptions =
      customer.subscriptions?.data[0].id &&
      (await fetchSubscription(customer.subscriptions?.data[0].id));

    if (!user?.subscription_id && subscriptions && user?.id) {
      const [subscriptionCreate] = await Promise.all([
        createSubscription(subscriptions, user.id),
      ]);

      await db.user.update({
        where: { id: user?.id },
        data: {
          subscription_id: subscriptionCreate.id,
          trial_ends_at: add(new Date(), { days: 0 }),
          subscription_role: subscriptionRoleByLimit(subscriptionCreate.limit),
        },
      });
    }
  }

  return redirect(
    rewriteLegacyVerifyPath(safeRedirectPath(redirectTo, APP_PAGE_URL)),
    {
      headers: headers,
    }
  );
}

export const meta: MetaFunction = () => [
  {
    title: "Login - Konnect",
  },
];

export default function LoginPage() {
  const zo = useZorm("LogInForm", LoginFormSchema);
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo") ?? undefined;

  const navigation = useNavigation();
  const disabled = isFormProcessing(navigation.state);
  const actionData = useActionData<typeof action>();

  return (
    <LoginCard title="Welcome back!">
      {/* <button className="mt-4 flex items-center justify-center rounded-lg border text-gray-600 transition-colors duration-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
              <div className="px-4 py-2">
                <svg className="h-6 w-6" viewBox="0 0 40 40">
                  <path
                    d="M36.3425 16.7358H35V16.6667H20V23.3333H29.4192C28.045 27.2142 24.3525 30 20 30C14.4775 30 10 25.5225 10 20C10 14.4775 14.4775 9.99999 20 9.99999C22.5492 9.99999 24.8683 10.9617 26.6342 12.5325L31.3483 7.81833C28.3717 5.04416 24.39 3.33333 20 3.33333C10.7958 3.33333 3.33335 10.7958 3.33335 20C3.33335 29.2042 10.7958 36.6667 20 36.6667C29.2042 36.6667 36.6667 29.2042 36.6667 20C36.6667 18.8825 36.5517 17.7917 36.3425 16.7358Z"
                    fill="#FFC107"
                  />
                  <path
                    d="M5.25497 12.2425L10.7308 16.2583C12.2125 12.59 15.8008 9.99999 20 9.99999C22.5491 9.99999 24.8683 10.9617 26.6341 12.5325L31.3483 7.81833C28.3716 5.04416 24.39 3.33333 20 3.33333C13.5983 3.33333 8.04663 6.94749 5.25497 12.2425Z"
                    fill="#FF3D00"
                  />
                  <path
                    d="M20 36.6667C24.305 36.6667 28.2167 35.0192 31.1742 32.34L26.0159 27.975C24.3425 29.2425 22.2625 30 20 30C15.665 30 11.9842 27.2359 10.5975 23.3784L5.16254 27.5659C7.92087 32.9634 13.5225 36.6667 20 36.6667Z"
                    fill="#4CAF50"
                  />
                  <path
                    d="M36.3425 16.7358H35V16.6667H20V23.3333H29.4192C28.7592 25.1975 27.56 26.805 26.0133 27.9758C26.0142 27.975 26.015 27.975 26.0158 27.9742L31.1742 32.3392C30.8092 32.6708 36.6667 28.3333 36.6667 20C36.6667 18.8825 36.5517 17.7917 36.3425 16.7358Z"
                    fill="#1976D2"
                  />
                </svg>
              </div>

              <span className="w-5/6 px-4 py-3 text-center font-bold">
                Sign in with Google
              </span>
            </button> */}

      {/* <div className="mt-4 flex items-center justify-between">
              <span className="w-1/5 border-b dark:border-gray-600 lg:w-1/4"></span>

              <div className="text-center text-xs uppercase text-gray-500 hover:underline dark:text-gray-400">
                or login with email
              </div>

              <span className="w-1/5 border-b dark:border-gray-400 lg:w-1/4"></span>
            </div> */}

      <Form ref={zo.ref} method="post" replace>
        <div className="mt-4">
          <label
            className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-200"
            htmlFor={zo.fields.email()}
          >
            Email Address
          </label>
          <input
            data-testid="email"
            autoFocus={true}
            name={zo.fields.email()}
            type="email"
            autoComplete="email"
            disabled={disabled}
            tabIndex={1}
            className="block w-full rounded-lg border bg-white px-4 py-2 text-gray-700 focus:outline-none focus:ring focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
          />
          {zo.errors.email()?.message && (
            <div className="pt-1 text-red-700" id="email-error">
              {zo.errors.email()?.message}
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="flex justify-between">
            <label
              className="mb-2 block text-sm font-medium text-gray-600 dark:text-gray-200"
              htmlFor={zo.fields.password()}
            >
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-xs text-gray-500 hover:underline dark:text-gray-300"
            >
              Forgot Password?
            </Link>
          </div>

          <input
            data-testid="password"
            name={zo.fields.password()}
            type="password"
            autoComplete="current-password"
            disabled={disabled}
            tabIndex={2}
            className="block w-full rounded-lg border bg-white px-4 py-2 text-gray-700 focus:outline-none focus:ring focus:ring-opacity-40 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300"
          />
          {zo.errors.password()?.message && (
            <div className="pt-1 text-red-700" id="password-error">
              {zo.errors.password()?.message}
            </div>
          )}
        </div>
        {actionData?.errors && (
          <div className="pt-1 text-red-700" id="password-error">
            {actionData.errors.toString()}
          </div>
        )}
        <div className="mt-6">
          <input
            type="hidden"
            name={zo.fields.redirectTo()}
            value={redirectTo}
          />
          <button
            data-testid="login"
            type="submit"
            disabled={disabled}
            tabIndex={3}
            className="w-full rounded-lg bg-purple px-6 py-3 text-sm font-medium capitalize tracking-wide text-white transition-colors duration-300 hover:bg-charcoal focus:outline-none focus:ring focus:ring-gray-300 focus:ring-opacity-50"
          >
            {disabled ? (
              <>
                <Spinner
                  aria-label="sign in"
                  className="fill-white text-purple"
                />
                <span className="pl-3">signing in...</span>
              </>
            ) : (
              `Sign In`
            )}
          </button>
        </div>
      </Form>
      {/* <div className="mt-4 flex items-center justify-between">
              <span className="w-1/5 border-b dark:border-gray-600 md:w-1/4"></span>

              <a
                href="#"
                className="text-xs uppercase text-gray-500 hover:underline dark:text-gray-400"
              >
                or sign up
              </a>

              <span className="w-1/5 border-b dark:border-gray-600 md:w-1/4"></span>
            </div> */}
    </LoginCard>
  );
}
